// components/ContentImportDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip,
} from '@mui/material';
import { Search, ImportExport } from '@mui/icons-material';
import { db } from '../lib/firebase';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';

const ContentImportDialog = ({ open, onClose, onImport }) => {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (open) {
      fetchContents();
    }
  }, [open]);

  const fetchContents = async () => {
    setLoading(true);
    try {
      const contentsRef = collection(db, 'contents');
      const q = query(contentsRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const contentsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
      }));
      setContents(contentsData);
    } catch (err) {
      console.error('Error fetching contents:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredContents = contents.filter(content =>
    content.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    content.content?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ImportExport />
          <Typography variant="h6">Import from Content Library</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          placeholder="Search content..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : filteredContents.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              No content available. Create some content first.
            </Typography>
          </Box>
        ) : (
          <List>
            {filteredContents.map((content) => (
              <ListItem
                key={content.id}
                disablePadding
                divider
                secondaryAction={
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      onImport(content);
                      onClose();
                    }}
                  >
                    Import
                  </Button>
                }
              >
                <ListItemButton onClick={() => {
                  onImport(content);
                  onClose();
                }}>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1">{content.title}</Typography>
                        <Chip
                          label={content.category}
                          size="small"
                          color={content.status === 'published' ? 'success' : 'default'}
                        />
                      </Box>
                    }
                    secondary={
                      <React.Fragment>
                        <Typography component="span" variant="body2" color="text.secondary">
                          {content.excerpt || content.content?.substring(0, 100)}...
                        </Typography>
                        <br />
                        <Typography component="span" variant="caption" color="text.secondary">
                          Created: {content.createdAt?.toLocaleDateString()} • By {content.author}
                        </Typography>
                        {content.tags && content.tags.length > 0 && (
                          <>
                            <br />
                            <Box display="flex" gap={0.5} mt={0.5}>
                              {content.tags.slice(0, 3).map((tag) => (
                                <Chip key={tag} label={tag} size="small" variant="outlined" />
                              ))}
                              {content.tags.length > 3 && (
                                <Chip label={`+${content.tags.length - 3}`} size="small" variant="outlined" />
                              )}
                            </Box>
                          </>
                        )}
                      </React.Fragment>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContentImportDialog;